import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ChevronLeft, Scale, Plus, Save, X } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';

// ── Adaptive Y-axis ticks (mismo enfoque que ProgressPage) ────────────────────
const computeTicks = (data) => {
  const weights = data.map((d) => d.weight).filter((w) => typeof w === 'number' && w > 0);
  if (weights.length === 0) return {};
  const minW = Math.min(...weights);
  const maxW = Math.max(...weights);
  if (minW === maxW) {
    const step = 0.5;
    return { ticks: [minW - step, minW, minW + step], domain: [minW - step * 2, minW + step * 2] };
  }
  const range = maxW - minW;
  const step = range <= 3 ? 0.25 : range <= 10 ? 0.5 : range <= 30 ? 1 : 2.5;
  const tickMin = Math.floor(minW / step) * step;
  const tickMax = Math.ceil(maxW / step) * step;
  const ticks = [];
  for (let v = tickMin - step; v <= tickMax + step + 0.001; v += step) {
    ticks.push(Math.round(v * 1000) / 1000);
  }
  return { ticks, domain: [tickMin - step, tickMax + step] };
};

const tickLabel = (v) => (v % 1 === 0 ? String(v) : String(parseFloat(v.toFixed(2))));

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '8px 12px', fontSize: 13, minWidth: 110,
    }}>
      <p style={{ color: 'var(--muted-foreground)', marginBottom: 4, fontSize: 11 }}>{label}</p>
      <p style={{ fontWeight: 700, color: 'var(--primary)', margin: 0 }}>
        {d?.weight?.toFixed(1)} kg
      </p>
    </div>
  );
};

// ── Modal "Añadir peso" ───────────────────────────────────────────────────────
const AddWeightModal = ({ onClose, onSaved, t }) => {
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [date, setDate] = useState(todayStr);
  const [weight, setWeight] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleSubmit = async () => {
    setError('');
    const raw = String(weight).replace(',', '.').trim();
    const num = parseFloat(raw);
    if (!raw || isNaN(num)) {
      setError(t('body_weight.error_invalid'));
      return;
    }
    if (num <= 0) {
      setError(t('body_weight.error_negative'));
      return;
    }
    if (num > 400) {
      setError(t('body_weight.error_too_high'));
      return;
    }
    if (!date) {
      setError(t('body_weight.error_date'));
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch('/api/body-weights', {
        method: 'POST',
        body: JSON.stringify({ weight: num, date }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        setError(err?.detail || t('common.error_connection'));
        setSaving(false);
        return;
      }
      const saved = await res.json();
      onSaved(saved);
    } catch (e) {
      setError(t('common.error_connection'));
      setSaving(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="add-weight-modal">
        <h2 style={{ textAlign: 'center', marginBottom: 4 }}>{t('body_weight.add_title')}</h2>
        <p style={{ textAlign: 'center', marginBottom: 12, color: 'var(--muted-foreground)', fontSize: 13 }}>
          {t('body_weight.add_subtitle')}
        </p>

        <div className="form-group">
          <label className="form-label">{t('body_weight.date_label')}</label>
          <input
            type="date"
            className="input"
            value={date}
            max={todayStr}
            onChange={(e) => setDate(e.target.value)}
            data-testid="weight-date-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label">{t('body_weight.weight_label')}</label>
          <div style={{ position: 'relative' }}>
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              min="0"
              className="input"
              placeholder={t('body_weight.weight_placeholder')}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              autoFocus
              style={{ paddingRight: 44 }}
              data-testid="weight-value-input"
            />
            <span style={{
              position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--muted-foreground)', fontSize: 14, fontWeight: 600, pointerEvents: 'none',
            }}>kg</span>
          </div>
        </div>

        {error && (
          <p style={{
            color: 'var(--destructive)', fontSize: 13, fontWeight: 600,
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.35)',
            borderRadius: 8, padding: '8px 12px', marginBottom: 4,
          }} data-testid="weight-error">
            {error}
          </p>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button
            className="btn btn-secondary"
            style={{ flex: 1 }}
            onClick={onClose}
            disabled={saving}
          >
            <X size={16} /> {t('common.cancel')}
          </button>
          <button
            className="btn btn-primary"
            style={{ flex: 1 }}
            onClick={handleSubmit}
            disabled={saving}
            data-testid="save-weight-btn"
          >
            <Save size={16} /> {saving ? t('common.loading') : t('body_weight.save_btn')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Página principal ─────────────────────────────────────────────────────────
const BodyWeightPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'es' ? 'es-ES' : 'en-US';

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const fetchEntries = useCallback(async () => {
    try {
      const res = await apiFetch('/api/body-weights');
      if (res.ok) setEntries(await res.json());
    } catch (e) {
      console.error('Error fetching body weights:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const chartData = useMemo(
    () => entries.map((e) => ({
      // Formato corto consistente con ProgressPage: MM/DD
      date: e.date.split('-').slice(1).join('/'),
      weight: e.weight,
      fullDate: e.date,
    })),
    [entries]
  );

  const { ticks, domain } = useMemo(() => computeTicks(chartData), [chartData]);

  const latest = entries.length > 0 ? entries[entries.length - 1] : null;
  const first = entries.length > 0 ? entries[0] : null;
  const delta = latest && first && latest !== first
    ? latest.weight - first.weight
    : null;

  const formatLongDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) return <div className="loading"><div className="loading-spinner" /></div>;

  const handleSaved = (saved) => {
    setShowAdd(false);
    // Insercion ordenada por fecha en lugar de refetch — mas rapido.
    setEntries((prev) => {
      const next = [...prev, saved];
      next.sort((a, b) => a.date.localeCompare(b.date));
      return next;
    });
  };

  return (
    <div className="page-container animate-fade-in" data-testid="body-weight-page">
      <button className="workout-page-back" onClick={() => navigate(-1)}>
        <ChevronLeft size={14} /> {t('common.back')}
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <h1 className="header-title" style={{ margin: 0 }}>{t('body_weight.page_title')}</h1>
        <Scale size={26} color="var(--primary)" />
      </div>

      {entries.length === 0 ? (
        <>
          <div className="empty-state">
            <Scale className="empty-icon" />
            <h3 className="empty-title">{t('body_weight.empty_title')}</h3>
            <p className="empty-desc">{t('body_weight.empty_desc')}</p>
            <button
              className="btn btn-primary"
              onClick={() => setShowAdd(true)}
              data-testid="add-first-weight-btn"
            >
              <Plus size={20} /> {t('body_weight.add_btn')}
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Resumen */}
          <div className="progress-grid" style={{ marginBottom: 16 }}>
            <div className="progress-card pc-stable">
              <span className="pc-label">
                <Scale size={12} /> {t('body_weight.latest_label')}
              </span>
              <span className="pc-value" style={{ color: 'var(--foreground)' }}>
                {latest.weight.toFixed(1)}
                <span style={{ fontSize: 16, marginLeft: 4, color: 'var(--muted-foreground)' }}>kg</span>
              </span>
              <span className="pc-sub">{formatLongDate(latest.date)}</span>
            </div>

            <div className={`progress-card ${delta == null ? 'pc-stable' : delta < 0 ? 'pc-improved' : delta > 0 ? 'pc-declined' : 'pc-stable'}`}>
              <span className="pc-label">{t('body_weight.delta_label')}</span>
              {delta == null ? (
                <>
                  <span className="pc-value">—</span>
                  <span className="pc-sub">{t('body_weight.delta_empty')}</span>
                </>
              ) : (
                <>
                  <span className="pc-value">
                    {delta > 0 ? '+' : ''}{delta.toFixed(1)}
                    <span style={{ fontSize: 16, marginLeft: 4, color: 'var(--muted-foreground)' }}>kg</span>
                  </span>
                  <span className="pc-sub">
                    {t('body_weight.delta_since', { date: formatLongDate(first.date) })}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Gráfico */}
          <div className="chart-container mb-4">
            <h3 className="chart-title">{t('body_weight.chart_title')}</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 16, right: 8, bottom: 0, left: 0 }}>
                <XAxis dataKey="date" stroke="var(--muted-foreground)" fontSize={10} />
                <YAxis
                  stroke="var(--muted-foreground)"
                  fontSize={10}
                  width={44}
                  ticks={ticks}
                  domain={domain}
                  tickFormatter={tickLabel}
                />
                <Tooltip content={<ChartTooltip />} />
                <Line
                  type="monotone"
                  dataKey="weight"
                  stroke="var(--primary)"
                  strokeWidth={2}
                  dot={{ r: 4, fill: 'var(--primary)', strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: 'var(--primary)' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Botón "Añadir peso" */}
          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            onClick={() => setShowAdd(true)}
            data-testid="add-weight-btn"
          >
            <Plus size={18} /> {t('body_weight.add_btn')}
          </button>
        </>
      )}

      {showAdd && (
        <AddWeightModal
          onClose={() => setShowAdd(false)}
          onSaved={handleSaved}
          t={t}
        />
      )}
    </div>
  );
};

export default BodyWeightPage;
